import React, { useState, useEffect } from 'react';
import { X, Gift, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../context/CartContext';

export function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const { cart } = useCart();

  useEffect(() => {
    // Check if already shown in this session
    const shown = sessionStorage.getItem('exit_popup_shown');
    if (shown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of viewport
      // AND user hasn't added anything to cart (don't disrupt buyers)
      if (e.clientY <= 0 && !hasShown && cart.length === 0) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exit_popup_shown', 'true');
      }
    };

    // Also show after 45 seconds if user hasn't interacted and cart is empty
    const timer = setTimeout(() => {
      if (!hasShown && !sessionStorage.getItem('exit_popup_shown') && cart.length === 0) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exit_popup_shown', 'true');
      }
    }, 45000); // Increased to 45 seconds

    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
    };
  }, [hasShown, cart.length]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText('WELCOME10');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-in fade-in duration-300">
      <div className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-orange-400 to-pink-500 p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Gift className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold">Wait! Don't Go Empty Handed</h2>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <p className="text-gray-600 mb-4">
            Here's a special gift for you! Get <span className="font-bold text-orange-600">10% OFF</span> on your first order.
          </p>

          {/* Coupon Code Box */}
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-4 mb-4">
            <p className="text-xs text-gray-500 mb-1">Your Discount Code</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl font-bold tracking-wider text-gray-800">WELCOME10</span>
              <button
                onClick={handleCopyCode}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Copy code"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
            {copied && <p className="text-xs text-green-600 mt-1">Copied!</p>}
          </div>

          <p className="text-xs text-gray-400 mb-4">
            Valid for first-time customers only. Min order ₹499.
          </p>

          <Button
            onClick={handleClose}
            className="w-full rounded-xl h-12 text-lg bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ExitIntentPopup;
